import {
  BaseField,
  type FieldData,
  FieldValidationLevel,
  type FieldValidationResult,
  FieldValidationType,
  ProductTypeEnum,
  type Quote,
  type Security,
  SecurityTypesUtils,
} from '@talos/kyoko';
import { entries, isArray } from 'lodash-es';
import type { AvaQuoteRequest } from '../../../providers/quotes.types';

interface Fixing {
  symbol: string;
  index: string | undefined;
}

export interface FixingFieldData extends FieldData<Fixing[]> {}

/**
 * FixingField encapsulates the logic needed for establishing the fixing details for a Quote Request. It
 * can be created from either a Security or a Quote entity. It runs validation on every change, and gives you a function
 * to get the QuoteRequest object you need to convey the user's selection to the backend on the QuoteRequest message.
 */
export class FixingField extends BaseField<FixingFieldData, Fixing[]> {
  constructor(initial?: Partial<FixingFieldData>) {
    super({
      name: 'Fixing Field',
      value: undefined,
      isRequired: false,
      isTouched: false,
      isDisabled: false,
      isVisible: true,
      placeholder: 'Select...',
      errors: [],
      isFocused: false,
      ...initial,
    });
  }

  static createFromSecurity(
    security: Security,
    tradableIndicesBySymbol: Map<string, Map<string, string[]>>
  ): FixingField {
    if (SecurityTypesUtils.Compound.isType(security)) {
      const legIndices = security.Legs.map(leg => {
        const tradableIndices = getTradableIndices([security.Symbol, leg.Symbol], tradableIndicesBySymbol);
        const shouldDefault = security.Legs.length > 1 || tradableIndices?.length === 1;
        return { symbol: leg.Symbol, index: shouldDefault ? tradableIndices?.at(0) : undefined };
      });

      return new FixingField({ value: legIndices }).validate();
    }

    const tradableIndices = getTradableIndices(security.Symbol, tradableIndicesBySymbol);
    return new FixingField({
      value: [{ symbol: security.Symbol, index: tradableIndices?.length === 1 ? tradableIndices?.at(0) : undefined }],
    }).validate();
  }

  static createFromQuote(quote: Pick<Quote, 'Symbol' | 'FixingDetails'>): FixingField {
    if (quote.FixingDetails?.MultilegFixingDetails) {
      // compound
      const legIndices: Fixing[] = entries(quote.FixingDetails.MultilegFixingDetails).map(([symbol, value]) => ({
        symbol,
        index: value.Index,
      }));

      return new FixingField({ value: legIndices }).validate();
    } else if (quote.FixingDetails) {
      // spot, so just one fixing entry
      return new FixingField({ value: [{ symbol: quote.Symbol, index: quote.FixingDetails.Index }] }).validate();
    }

    return new FixingField().validate();
  }

  /** Writes to the internal data array at the position of this index. Returns a new FixingField instance */
  setFixing(symbol: string, index: string | undefined): FixingField {
    if (!this.value) {
      return this;
    }

    const updatedFixings = this.value?.map(fixing => (fixing.symbol === symbol ? { symbol, index } : fixing));
    return this.updateValue(updatedFixings);
  }

  /** Given a symbol, returns the selected index for this symbol (string or undefined if nothing selected) */
  getSelectedIndexForSymbol(symbol: string): string | undefined {
    return this.data.value?.find(fixing => fixing.symbol === symbol)?.index;
  }

  /** Produces the FixingDetails property you need to put in your QuoteRequest message */
  getQuoteRequestFixingDetails(productType: ProductTypeEnum): AvaQuoteRequest['FixingDetails'] {
    if (!this.value) {
      return undefined;
    }

    if (productType === ProductTypeEnum.Compound) {
      // produces something like { MultilegFixingDetails: { "BTC-USD": { Index: "BRRNY"}, "ETH-USD": { Index: "ECX" } } }
      return this.value.reduce(
        (fixingDetails, entry) => {
          if (entry.index == null) {
            return fixingDetails;
          }

          fixingDetails.MultilegFixingDetails[entry.symbol] = { Index: entry.index };
          return fixingDetails;
        },
        { MultilegFixingDetails: {} as { [key: string]: { Index: string } } }
      );
    } else {
      // Non-compound instruments (no legs) still just put { Index: "whatever" } instead of having a per-leg symbol breakdown
      const index0 = this.value.at(0)?.index;
      return index0 ? { Index: index0 } : undefined;
    }
  }

  get value() {
    return this.data.value;
  }

  updateValue(value: Fixing[]): FixingField {
    const updated = this.updateData({ value, isTouched: true });
    return updated.invariantCheck();
  }

  setTouched(isTouched: boolean): FixingField {
    const updated = this.updateData({ isTouched });
    return updated.invariantCheck();
  }

  setIsVisible(isVisible: boolean): FixingField {
    const updated = this.updateData({ isVisible });
    return updated.invariantCheck();
  }

  setIsRequired(isRequired: boolean): FixingField {
    const updated = this.updateData({ isRequired });
    return updated.invariantCheck();
  }

  setDisabled(isDisabled: boolean): FixingField {
    const updated = this.updateData({ isDisabled });
    return updated.invariantCheck();
  }

  validate(): FixingField {
    // other fields accept rules as a parameter here. we dont have that yet, but we might...
    return this.invariantCheck();
  }

  public override setFocused(isFocused: boolean): FixingField {
    return this.updateData({ isFocused });
  }

  private invariantCheck(): FixingField {
    const errors: FieldValidationResult[] = this.data.errors.filter(e => e.type === FieldValidationType.Rule);

    if (!this.isRequired) {
      return this.updateData({ errors: [] });
    }

    // 1. There must be at least one array entry
    if (this.value == null || this.value.length === 0) {
      errors.push({
        message: 'No fixing index selected',
        level: FieldValidationLevel.Error,
      });
    }

    // 2. Every entry in the value array must have a defined index
    if (this.value?.some(entry => entry.index == null)) {
      const singular = this.value.length === 1;
      errors.push({
        message: singular ? 'Missing fixing index' : 'Missing fixing indices',
        level: FieldValidationLevel.Error,
      });
    }

    return this.updateData({ errors });
  }

  private updateData(data: Partial<FixingFieldData>): FixingField {
    return new FixingField({ ...this.data, ...data });
  }
}

// Just a little helper to interact with the double-map tradableIndicesBySymbol
function getTradableIndices(
  symbolOrSymbols: string | [string, string],
  tradableIndicesBySymbol: Map<string, Map<string, string[]>>
): string[] | undefined {
  const [firstSymbol, secondSymbol]: [string, string] = isArray(symbolOrSymbols)
    ? symbolOrSymbols
    : [symbolOrSymbols, symbolOrSymbols];

  return tradableIndicesBySymbol.get(firstSymbol)?.get(secondSymbol);
}
